import type { PropsWithChildren } from 'react'
import React, { useMemo } from 'react'
import type { CueContextProps } from '../model/props'
import { CueContext } from '../lib/cue-context'

/**
 * CueProvider is a React functional component that provides the CueContext to its child components.
 * It uses React's Context API to share the `cueId` value across the component tree without the need
 * for props drilling. The provided `cueId` value can be consumed by components using the CueContext.
 *
 * The `cueId` value is memoized to ensure that consumers of this context re-render only when
 * the `cueId` changes.
 */
export const CueProvider: React.FC<PropsWithChildren<CueContextProps>> = ({
  cueId,
  children,
}) => {
  // Memoize the context value
  const value = useMemo(() => ({ cueId }), [cueId])

  return <CueContext.Provider value={value}>{children}</CueContext.Provider>
}
